// Pet Arena: her pet versus a wild one, turn by turn. Gentle: nobody is ever lost, a beaten
// pet just needs a nap. A Study question once per fight charges every move.

import { h, clear } from '@kit/dom';
import { makeSurface } from '@kit/canvas';
import { makeLoop } from '@kit/loop';
import { pick, shuffle } from '@kit/rng';
import type { GameModule, GameContext } from '@shell/types';
import type { PetType } from '@shell/state';
import { PET_TYPES, drawPet } from '@shell/pets';
import { QUIZ } from '@content/science/quiz';
import { CLUE_WORDS } from '@content/words/clues';
import { makeFighter, tickCharges, isReady, chooseAiMove, applyMove, chargeAll, wildStage, LUCKY_CHANCE, LUCKY_BONUS, REST_HEAL, MOVES, TYPE_MOVE_NAMES, BIG_MOVE_NAMES, BEATS, type Fighter, type MoveKind } from './battle';

interface OwnedPet { id: string; name: string; type: PetType; stage: 1 | 2 | 3 }
interface Question { q: string; choices: string[]; answer: number; why: string }
interface Float { x: number; y: number; text: string; color: string; t: number }

const WILD_NAMES: Record<PetType, string[]> = {
  stone: ['Pebble', 'Boulder', 'Flint'], leaf: ['Sprout', 'Fern', 'Moss'], spark: ['Zippy', 'Flash', 'Bolt'],
  wave: ['Ripple', 'Splash', 'Tide'], frost: ['Flurry', 'Icicle', 'Snowy'], star: ['Twinkle', 'Comet', 'Nova'],
};
const TYPES: PetType[] = ['stone', 'leaf', 'spark', 'wave', 'frost', 'star'];

function mount(host: HTMLElement, ctx: GameContext) {
  const pets: OwnedPet[] = (ctx.pets ?? []) as OwnedPet[];
  if (!pets.length) {
    host.append(h('div.card.center', { style: { marginTop: '16px' } },
      h('h3', null, 'Hatch a pet in the Nest first'),
      h('p.muted', null, 'Eggs appear when you earn charms and stars. Warm one by finishing learning rounds, hatch it in the Nest at your house, then come back and it can play here.'),
    ));
    return { destroy() { clear(host); } };
  }

  let chosen: OwnedPet = pets.find(p => p.id === ctx.activePet) ?? pets[0];
  let fights = 0, wins = 0;
  let a!: Fighter, b!: Fighter;
  let turn: 'her' | 'wild' | 'busy' | 'over' = 'busy';
  let studyUsed = false;
  let lastHerMove: MoveKind | null = null;
  let lastEff: string = 'normal';
  let floats: Float[] = [];
  let celebrate = 0, t = 0, wobble = 0;
  let ended = false;
  const used = new Set<string>();
  const t0 = performance.now();

  const head = h('div.row', { style: { justifyContent: 'space-between' } }, h('span.muted', null, 'Fight ', h('strong', null, '1'), ' of 3'), h('span.muted', null, 'Wins: ', h('strong', null, '0')));
  const stage = h('div');
  const panel = h('div.stack', { style: { gap: '8px' } });
  host.append(head, stage, panel);
  const S = makeSurface(stage, { aspect: 0.7, draw: false });

  // ---------- pick ----------
  function showPick() {
    clear(panel);
    panel.append(
      h('p.center.muted.small', null, 'Who is fighting today?'),
      h('div.grid2', null, ...pets.map(p => h('button.btn' + (p.id === chosen.id ? '.primary' : ''), { onclick: () => { chosen = p; showPick(); } }, `${p.name}`, h('span.small', { style: { opacity: '.8', marginLeft: '6px' } }, `${PET_TYPES[p.type].name} ${p.stage}`)))),
      h('div.grid2', null,
        h('button.btn', { onclick: showTypes }, 'Who beats whom?'),
        h('button.btn.primary', { onclick: startFight }, 'Into the ring'),
      ),
    );
    draw();
  }

  function showTypes() {
    clear(panel);
    const rows = (Object.keys(BEATS) as PetType[]).map(k => h('div.row', null,
      h('span.pill', { style: { background: PET_TYPES[k].color, color: '#fff', borderColor: PET_TYPES[k].color } }, PET_TYPES[k].name),
      h('span.muted', null, 'beats'),
      h('span.pill', { style: { background: PET_TYPES[BEATS[k]!].color, color: '#fff', borderColor: PET_TYPES[BEATS[k]!].color } }, PET_TYPES[BEATS[k]!].name),
    ));
    panel.append(
      h('div.card.stack', null, h('h3', null, 'The type wheel'), ...rows, h('p.muted.small', null, 'Star is fair with everyone. A move that beats the other type does one and a half times the damage; one that is beaten does two thirds. Tackle ignores types.')),
      h('button.btn.primary.block', { onclick: () => turn === 'over' || !a ? showPick() : showControls() }, 'Back'),
    );
  }

  // ---------- fight ----------
  function startFight() {
    fights++;
    a = makeFighter(chosen.name, chosen.type, chosen.stage);
    const wt = pick(ctx.rng, TYPES);
    const ws = wildStage(chosen.stage, ctx.rng());
    b = makeFighter(pick(ctx.rng, WILD_NAMES[wt]), wt, ws);
    studyUsed = false; lastHerMove = null; floats = []; celebrate = 0;
    (head.firstElementChild!.querySelector('strong') as HTMLElement).textContent = String(fights);
    ctx.say(`A wild ${PET_TYPES[wt].name} pet, ${b.name}, hops into the ring!`);
    turn = 'her';
    tickCharges(a);
    showControls();
  }

  function ring(ready: boolean, frac: number, color: string): string {
    return ready ? `conic-gradient(${color} 360deg, ${color} 0)` : `conic-gradient(${color} ${Math.round(frac * 360)}deg, var(--line) 0)`;
  }

  function moveBtn(kind: Exclude<MoveKind, 'rest'>, label: string, color: string, sub: string): HTMLElement {
    const ready = isReady(a, kind);
    const total = MOVES[kind].chargeTurns || 1;
    const left = kind === 'tackle' ? 0 : a.charge[kind];
    const frac = 1 - left / total;
    return h('button.btn', { disabled: turn !== 'her' || !ready, onclick: () => herMove(kind), style: { justifyContent: 'space-between', minHeight: '56px' } },
      h('span', { style: { textAlign: 'left' } }, h('div', null, label), h('div.small.muted', null, sub)),
      h('span', { style: { width: '26px', height: '26px', borderRadius: '50%', background: ring(ready, frac, color), display: 'inline-block', flex: '0 0 auto' } }),
    );
  }

  function showControls() {
    clear(panel);
    const tc = PET_TYPES[a.type].color;
    panel.append(
      h('div.grid2', null,
        moveBtn('tackle', 'Tackle', '#9a9aa3', '4 damage, always ready'),
        moveBtn('type', TYPE_MOVE_NAMES[a.type], tc, `7 damage, ${PET_TYPES[a.type].name} move`),
        moveBtn('big', BIG_MOVE_NAMES[a.type], PET_TYPES[a.type].color2, '10 damage, slow to charge'),
        h('button.btn', { disabled: turn !== 'her', onclick: () => herMove('rest'), style: { minHeight: '56px' } }, `Rest (+${REST_HEAL})`),
      ),
      h('div.grid2', null,
        h('button.btn.small', { disabled: turn !== 'her' || studyUsed, onclick: showStudy }, studyUsed ? 'Studied this fight' : 'Study (+gems, charge all)'),
        h('button.btn.small.ghost', { onclick: showTypes }, 'Who beats whom?'),
      ),
    );
  }

  function herMove(kind: MoveKind) {
    if (turn !== 'her') return;
    turn = 'busy';
    const lucky = kind !== 'rest' && ctx.rng() < LUCKY_CHANCE;
    const r = applyMove(a, b, kind, lucky);
    lastHerMove = kind; lastEff = r.eff;
    narrate(a, b, kind, r, lucky);
    showControls();
    if (b.hp <= 0) { setTimeout(() => endFight(true), 700); return; }
    setTimeout(wildTurn, 900);
  }

  function wildTurn() {
    tickCharges(b);
    const kind = chooseAiMove(b);
    const lucky = kind !== 'rest' && ctx.rng() < LUCKY_CHANCE;
    const r = applyMove(b, a, kind, lucky);
    narrate(b, a, kind, r, lucky);
    if (a.hp <= 0) { setTimeout(() => endFight(false), 700); return; }
    setTimeout(() => { turn = 'her'; tickCharges(a); showControls(); }, 900);
  }

  function narrate(from: Fighter, to: Fighter, kind: MoveKind, r: { dealt: number; healed: number; eff: string }, lucky: boolean) {
    const W = S.w, H = S.h;
    const targetX = to === a ? W * 0.28 : W * 0.72;
    if (kind === 'rest') {
      floats.push({ x: from === a ? W * 0.28 : W * 0.72, y: H * 0.45, text: `+${r.healed}`, color: '#5fbf6a', t: 0 });
      ctx.sound('tick');
      ctx.say(`${from.name} takes a rest and heals ${r.healed}.`);
      return;
    }
    const name = kind === 'tackle' ? 'Tackle' : kind === 'type' ? TYPE_MOVE_NAMES[from.type] : BIG_MOVE_NAMES[from.type];
    floats.push({ x: targetX, y: H * 0.45, text: `-${r.dealt}`, color: r.eff === 'super' ? '#e2574c' : r.eff === 'weak' ? '#9a9aa3' : '#2a2a2e', t: 0 });
    if (lucky) floats.push({ x: targetX, y: H * 0.36, text: `lucky +${LUCKY_BONUS}!`, color: '#f5c542', t: -0.2 });
    wobble = 0.5;
    ctx.sound(to.hp <= 0 ? 'buzz' : 'pop');
    const effText = r.eff === 'super' ? ' It is super effective!' : r.eff === 'weak' ? ' It is not very effective.' : '';
    ctx.say(`${from.name} uses ${name} for ${r.dealt}.${effText}${lucky ? ' Lucky hit!' : ''}`);
  }

  function endFight(won: boolean) {
    turn = 'over';
    if (won) {
      wins++; celebrate = 2.2;
      ctx.sound('ding');
      ctx.say(`${b.name} wanders off. ${a.name} wins!`);
      if (lastHerMove && lastHerMove !== 'tackle' && lastHerMove !== 'rest' && lastEff === 'super') ctx.charm('charm.typemaster');
    } else {
      ctx.say(`${a.name} needs a nap. ${b.name} wanders off, and nothing is lost.`);
    }
    (head.lastElementChild!.querySelector('strong') as HTMLElement).textContent = String(wins);
    clear(panel);
    const done = fights >= 3 || wins >= 2;
    panel.append(
      h('div.card.center', null, h('h3', null, won ? `${a.name} wins!` : `${a.name} needs a nap`), h('p.muted.small', null, won ? 'A little victory dance.' : 'A snack and a cuddle and your pet is ready again.')),
      done
        ? h('button.btn.primary.block', { onclick: finish }, 'Finish')
        : h('div.grid2', null, h('button.btn', { onclick: showPick }, 'Swap pet'), h('button.btn.primary', { onclick: startFight }, `Fight ${fights + 1}`)),
    );
  }

  function finish() {
    if (ended) return;
    ended = true;
    if (wins >= 2 && fights === 2) ctx.charm('charm.arena');
    if (wins >= 2) ctx.sound('chime');
    ctx.end({ attempts: fights, correct: wins, perfect: wins === fights && fights >= 2, durationMs: performance.now() - t0, notes: [`${chosen.name} won ${wins} of ${fights}`] });
  }

  // ---------- study ----------
  function nextQuestion(): Question {
    const quiz = QUIZ.filter(q => q.level <= 2 && !used.has(q.id));
    const clues = CLUE_WORDS.filter(w => w.tier === 1 && !used.has('clue:' + w.word));
    if (quiz.length && (ctx.rng() < 0.6 || !clues.length)) {
      const q = pick(ctx.rng, quiz); used.add(q.id);
      const right = q.choices[q.answer];
      const choices = shuffle(ctx.rng, q.choices.slice());
      return { q: q.q, choices, answer: choices.indexOf(right), why: q.why };
    }
    const w = pick(ctx.rng, clues); used.add('clue:' + w.word);
    const others = shuffle(ctx.rng, CLUE_WORDS.filter(x => x.tier === 1 && x.word !== w.word)).slice(0, 3).map(x => x.word);
    const choices = shuffle(ctx.rng, [w.word, ...others]);
    return { q: `${w.clues[0]} ${w.clues[1]} What is it?`, choices, answer: choices.indexOf(w.word), why: w.clues[4] };
  }

  function showStudy() {
    if (turn !== 'her' || studyUsed) return;
    studyUsed = true; turn = 'busy';
    const q = nextQuestion();
    clear(panel);
    panel.append(
      h('div.card', null, h('p.muted.small', null, `${a.name} opens a book:`), h('h3', null, q.q)),
      h('div.grid2', null, ...q.choices.map((c, i) => h('button.btn', { onclick: () => answer(i) }, c))),
    );
    function answer(i: number) {
      if (i === q.answer) {
        chargeAll(a);
        ctx.sound('chime');
        ctx.award(10, 'study break');
        ctx.say(`Right! ${q.why} Every move is charged.`);
        turn = 'her';
        showControls();
      } else {
        ctx.sound('buzz');
        ctx.say(`Not quite. ${q.why} The turn passes.`);
        showControls();
        setTimeout(wildTurn, 900);
      }
    }
  }

  // ---------- drawing ----------
  function update(dt: number) {
    t += dt;
    if (wobble > 0) wobble -= dt;
    if (celebrate > 0) celebrate -= dt;
    for (const f of floats) f.t += dt;
    floats = floats.filter(f => f.t < 1.4);
  }

  function hpBar(c: CanvasRenderingContext2D, f: Fighter, x: number, y: number, w: number) {
    c.fillStyle = '#2a2a2e'; c.font = '800 13px Nunito, sans-serif'; c.textAlign = 'left'; c.textBaseline = 'alphabetic';
    c.fillText(`${f.name}  ${PET_TYPES[f.type].name} ${f.stage}`, x, y - 6);
    c.fillStyle = 'var(--line)'; c.fillStyle = '#e2dccb'; c.fillRect(x, y, w, 10);
    const frac = f.hp / f.maxHp;
    c.fillStyle = frac > 0.5 ? '#5fbf6a' : frac > 0.25 ? '#f5c542' : '#e2574c';
    c.fillRect(x, y, Math.round(w * frac), 10);
    c.fillStyle = '#2a2a2e'; c.font = '700 12px Nunito, sans-serif'; c.textAlign = 'right';
    c.fillText(`${Math.round(f.hp)} / ${f.maxHp}`, x + w, y + 22);
  }

  function draw() {
    const c = S.ctx, W = S.w, H = S.h;
    // storybook backdrop
    c.fillStyle = '#d9ecf7'; c.fillRect(0, 0, W, H);
    c.fillStyle = '#b7dca6'; c.beginPath(); c.ellipse(W * 0.2, H * 0.55, W * 0.5, H * 0.2, 0, 0, 7); c.fill();
    c.fillStyle = '#a3d08f'; c.beginPath(); c.ellipse(W * 0.85, H * 0.58, W * 0.45, H * 0.18, 0, 0, 7); c.fill();
    c.fillStyle = '#9ad67e'; c.fillRect(0, H * 0.6, W, H * 0.4);
    // ring
    c.strokeStyle = '#f6f3ec'; c.lineWidth = 6; c.beginPath(); c.ellipse(W / 2, H * 0.8, W * 0.42, H * 0.14, 0, 0, 7); c.stroke();
    c.fillStyle = 'rgba(255,255,255,.25)'; c.fill();
    if (!a || !b) {
      // pick screen: show the chosen pet alone
      drawPet(c, chosen, W * 0.5, H * 0.8, Math.max(5, W / 60), Math.floor(t * 2) % 2);
      c.fillStyle = '#2a2a2e'; c.font = '800 16px Nunito, sans-serif'; c.textAlign = 'center'; c.textBaseline = 'alphabetic';
      c.fillText(chosen.name, W * 0.5, H * 0.95);
      return;
    }
    const sc = Math.max(5, W / 60);
    const shakeA = wobble > 0 && turn !== 'her' ? Math.sin(t * 40) * 3 : 0;
    const shakeB = wobble > 0 && turn === 'busy' ? Math.sin(t * 40) * 3 : 0;
    const hopA = celebrate > 0 ? Math.abs(Math.sin(t * 8)) * 14 : 0;
    const frame = Math.floor(t * 3) % 2;
    if (a.hp > 0 || celebrate > 0) drawPet(c, a, W * 0.28 + shakeA, H * 0.82 - hopA, sc, frame); else drawNap(c, a, W * 0.28, H * 0.82, sc);
    if (b.hp > 0) drawPet(c, b, W * 0.72 + shakeB, H * 0.82, sc * (0.9 + b.stage * 0.05), frame); else drawNap(c, b, W * 0.72, H * 0.82, sc);
    hpBar(c, a, W * 0.06, H * 0.12, W * 0.38);
    hpBar(c, b, W * 0.56, H * 0.12, W * 0.38);
    // floats
    for (const f of floats) {
      const k = Math.max(0, f.t);
      c.globalAlpha = Math.max(0, 1 - k / 1.4);
      c.fillStyle = f.color; c.font = '900 20px Nunito, sans-serif'; c.textAlign = 'center'; c.textBaseline = 'middle';
      c.fillText(f.text, f.x, f.y - k * 40);
      c.globalAlpha = 1;
    }
    if (celebrate > 0) {
      c.fillStyle = '#f5c542';
      for (let i = 0; i < 12; i++) { const ang = i * 0.52 + t * 2; const r = 40 + (2.2 - celebrate) * 50; c.beginPath(); c.arc(W * 0.28 + Math.cos(ang) * r, H * 0.6 + Math.sin(ang) * r * 0.6, 4, 0, 7); c.fill(); }
    }
    if (turn === 'her') { c.fillStyle = '#2a2a2e'; c.font = '800 13px Nunito, sans-serif'; c.textAlign = 'center'; c.textBaseline = 'alphabetic'; c.fillText('Your move', W * 0.28, H * 0.97); }
  }

  function drawNap(c: CanvasRenderingContext2D, f: Fighter, x: number, y: number, sc: number) {
    c.save(); c.globalAlpha = 0.6; drawPet(c, f, x, y, sc * 0.9, 0); c.restore();
    c.fillStyle = '#2a2a2e'; c.font = '800 14px Nunito, sans-serif'; c.textAlign = 'center'; c.textBaseline = 'alphabetic';
    c.fillText('z z z', x + sc * 4, y - sc * 7);
  }

  const loop = makeLoop(update, draw);
  loop.start();
  showPick();
  return { destroy() { loop.stop(); S.destroy(); clear(host); } };
}

const mod: GameModule = {
  id: 'fun.pet-arena',
  title: 'Pet Arena',
  blurb: 'Your pet versus a wild one, turn by turn. Types matter. Nobody gets hurt for real.',
  category: 'fun',
  skills: [],
  levelRange: [1, 10],
  unlock: { gems: 220 },
  mount,
};
export default mod;
